import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  type OnDestroy,
  type OnInit,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Subscription, type Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext, LoaderType } from '@fcom/common/interfaces';
import { unsubscribe } from '@fcom/core/utils';
import { FormStatus } from '@fcom/service-forms';
import {
  ATTACHMENT_ALLOWED_FILE_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_IN_BYTES,
  ButtonMode,
  ButtonTheme,
  ButtonType,
  MAX_ATTACHMENTS_10,
  NotificationLayout,
  NotificationTheme,
  TextInputComponent,
  TextInputType,
} from '@fcom/ui-components';
import { FileUploaderComponent } from '@fcom/ui-components/components/forms/file-uploader/file-uploader.component';
import type { AttachmentResponse } from '@fcom/ui-components/components/forms/interfaces';
import type { Attachment } from '@fcom/ui-components/components/interfaces';

import { FileOrigin, type CaseGetMessage, type RetrieveCase } from '../../../interfaces';
import { ClaimOrFeedbackService } from '../../../services/claim-or-feedback.service';
import { formatUserInput } from '../../../utils';

export enum ChatterFields {
  CHATTER_FORM = 'chatterForm',
  MESSAGE = 'message',
  FILES = 'files',
  ATTACHMENTS = 'attachments',
}

@Component({
  selector: 'fin-chatter',
  templateUrl: './chatter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatterComponent implements OnInit, OnDestroy {
  readonly allowedFileTypes: FileUploaderComponent['allowedFileTypes'] = ATTACHMENT_ALLOWED_FILE_EXTENSIONS;
  readonly maxAttachments: FileUploaderComponent['maxAttachments'] = MAX_ATTACHMENTS_10;
  readonly maxSizeInBytes: FileUploaderComponent['maxSizeInBytes'] = ATTACHMENT_MAX_SIZE_IN_BYTES;
  readonly messageLimit = 3000;

  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly ChatterFields = ChatterFields;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly TextInputType = TextInputType;

  @Input() caseResponse: RetrieveCase;

  @Output() caseMessage: EventEmitter<CaseGetMessage> = new EventEmitter<CaseGetMessage>();

  @ViewChild('fileUploader') fileUploader: FileUploaderComponent;
  @ViewChild('message') message: TextInputComponent;

  formStatus: FormStatus = FormStatus.INITIAL;
  reactiveForm: UntypedFormGroup = new UntypedFormGroup({});
  group: UntypedFormGroup;

  private subscription = new Subscription();

  constructor(
    private cdr: ChangeDetectorRef,
    private claimOrFeedbackService: ClaimOrFeedbackService,
    private fb: UntypedFormBuilder,
    private gtmService: GtmService
  ) {}

  get attachments(): Attachment[] {
    return this.fileUploader.attachments;
  }

  get characterCount(): number {
    return this.message?.control?.value?.length || 0;
  }

  ngOnInit(): void {
    this.group = this.fb.group({
      [ChatterFields.ATTACHMENTS]: [],
      [ChatterFields.MESSAGE]: [
        this.reactiveForm?.get(ChatterFields.MESSAGE)?.value || '',
        [Validators.required, Validators.maxLength(this.messageLimit)],
      ],
    });

    this.reactiveForm.setControl(ChatterFields.CHATTER_FORM, this.group);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  sendMessage(): void {
    const attachmentIds: string[] = this.attachments.filter(Boolean).map((a: Attachment): string => a.fileId);
    const message: string = formatUserInput(this.group.get(ChatterFields.MESSAGE)?.value || '');
    if (message) {
      this.formStatus = FormStatus.LOADING;
      this.group.reset();

      this.subscription.add(
        this.claimOrFeedbackService
          .sendMessageToCase(this.caseResponse?.caseId, this.caseResponse?.customer?.lastname, message, attachmentIds)
          .pipe(take(1))
          .subscribe({
            next: (caseMessage: CaseGetMessage): void => {
              this.formStatus = FormStatus.INITIAL;
              this.caseMessage.emit(caseMessage);
              this.trackElement('success');
              this.cdr.markForCheck();
            },
            error: (): void => {
              this.trackElement('error');
              this.formStatus = FormStatus.ERROR;
              this.cdr.markForCheck();
            },
          })
      );
    }
  }

  uploadFile(): (f: File) => Observable<AttachmentResponse> {
    return (file: File): Observable<AttachmentResponse> => {
      return this.claimOrFeedbackService.uploadFile<AttachmentResponse>(file, FileOrigin.FEED);
    };
  }

  private trackElement(event: 'success' | 'error'): void {
    this.gtmService.trackElement(
      `send-message-${event}`,
      GaContext.CHATTER_MESSAGE_FORM,
      ElementTypes.FORM,
      undefined,
      ElementActions.SUBMIT
    );
  }
}
